import { z } from "zod";
import { logError } from "../core/logger";

export function createIngestor<T extends z.ZodType, API extends Record<string, any>>(
  schema: T,
  api: API,
  entityName: string,
) {
  type ValidatedEntity = z.infer<T>;

  // Methods that return different types and should skip validation
  const skipValidationMethods = [
    "fetchPracticeManagerByLocation",
    "fetchAmenitiesForLocation",
    "fetchAmenityById",
    "findRooms",
    "fetchBookingCountsByLocation",
    "fetchMemberById", // Skip validation for fetchMemberById to allow partial/redacted profiles
    "fetchMembersByIds", // Returns { members, missingIds } — not a single Member
    "fetchAllRooms", // Returns Room[] but the response envelope confuses the paginated detector
    "fetchNotices", // Returns Notice[] instead of Location[]
    "createNotice", // Returns Notice instead of Location
    "broadcastNotice", // Returns { success, count, notices } instead of Location
    "deleteNotice", // Returns { success: boolean, message: string }
    "fetchMembersICanMessage", // Returns MessageableEntity[] instead of Member
    "checkRecurrenceAvailability", // Returns CheckRecurrenceAvailabilityResponse
    "addRecurrence", // Returns AddRecurrenceResponse
    "removeRecurrence", // Returns RemoveRecurrenceResponse
    "previewPermanentDeletion", // Returns DeletionPreview instead of Member
    "permanentlyDeleteMember", // Returns PermanentDeletionResponse instead of Member
    "getOrganisationLocationBookingStatus", // Returns { lockedLocationIds } instead of Organisation
    "getLastLoginCode", // Returns { code, expiresAt } | { code: null } instead of Member
    "getOccurrenceHistory", // Returns { occurrences, cappedAt52 } — not a Booking
    "getUpgradeToSlotPreview", // Returns UpgradeToSlotPreview — not a Booking
  ];

  return new Proxy(api, {
    get(target, prop) {
      const original = target[prop as keyof API];

      if (typeof original !== "function") {
        return original;
      }

      return async (...args: any[]) => {
        const result = await (original as Function).apply(target, args);

        // Skip validation for methods that return different entity types
        if (skipValidationMethods.includes(prop.toString())) {
          return result;
        }

        // Skip validation for paginated responses - validate the data array instead
        const isPaginatedResponse =
          result &&
          typeof result === "object" &&
          "data" in result &&
          Array.isArray(result.data) &&
          "pagination" in result;

        if (isPaginatedResponse) {
          // Resilient ingest: validate each row independently and DROP the invalid ones (with a
          // logged reason) rather than rejecting the whole page. One malformed record must never
          // white-screen the app — the user still sees every valid row.
          const validatedData = parseArrayResilient(result.data, prop.toString());
          return {
            ...result,
            data: validatedData,
          };
        }

        // Skip validation for API response objects (e.g., { success: boolean, message: string })
        const isApiResponse =
          result && typeof result === "object" && ("success" in result || "message" in result) && !("id" in result);

        // validate single entity results
        if (
          result &&
          typeof result === "object" &&
          !Array.isArray(result) &&
          !(result instanceof Error) &&
          !isApiResponse
        ) {
          const parsed = schema.safeParse(result);
          if (parsed.success) {
            return parsed.data as ValidatedEntity;
          }
          // Best-effort: log the shape mismatch but return the raw payload instead of throwing a
          // fatal "Invalid data" error that white-screens the app. Callers degrade gracefully on
          // any missing field rather than losing the whole screen.
          logError(`${entityName}Ingestor`, `Validation failed for ${prop.toString()} — returning raw`, parsed.error);
          return result as ValidatedEntity;
        }

        // validate array results
        if (Array.isArray(result)) {
          return parseArrayResilient(result, prop.toString());
        }
        return result;
      };

      function parseArrayResilient(items: any[], methodName: string): ValidatedEntity[] {
        const valid: ValidatedEntity[] = [];
        let dropped = 0;
        for (const item of items) {
          const parsed = schema.safeParse(item);
          if (parsed.success) {
            valid.push(parsed.data as ValidatedEntity);
          } else {
            dropped++;
            logError(`${entityName}Ingestor`, `Dropping invalid ${entityName} from ${methodName}`, parsed.error);
          }
        }
        if (dropped > 0) {
          logError(
            `${entityName}Ingestor`,
            `${methodName}: dropped ${dropped} invalid ${entityName} record(s), kept ${valid.length}`,
            new Error("partial ingest"),
          );
        }
        return valid;
      }
    },
  }) as API;
}
